import { useState, useEffect } from 'react';
import { SEO } from '../../utils/seo';

export default function InstagramPage() {
  const [isHovered, setIsHovered] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const instagramSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Instagram - Luan Lara Psicólogo',
    description: 'Acompanhe o conteúdo do psicólogo Luan Lara no Instagram. Posts sobre psicologia clínica e psicologia do futebol.',
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/instagram`,
    inLanguage: 'pt-BR',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Luan Lara Psicólogo',
      url: import.meta.env.VITE_SITE_URL || 'https://example.com'
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Instagram - Luan Lara Psicólogo | Psicologia Clínica e do Futebol"
        description="Acompanhe o conteúdo do psicólogo Luan Lara no Instagram. Posts sobre psicologia clínica e psicologia do futebol."
        keywords="instagram, psicologia clínica, psicologia do futebol, luan lara"
        canonical="/instagram"
        schema={instagramSchema}
      />
      {/* Header */}
      <header className="bg-white shadow-sm fixed w-full top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 flex-shrink-0">
                <img
                  src="https://static.readdy.ai/image/9df808c5a5eda92f2f3db0cc4cefbc45/a965b4996718a266233e16beeb49802c.png"
                  alt="Luan Lara Psicólogo Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Luan Lara PSICÓLOGO</h1>
                <p className="text-sm text-gray-600">Psicologia Clínica & Esportiva</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => window.REACT_APP_NAVIGATE('/')} className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Início</button>
              <button onClick={() => { window.REACT_APP_NAVIGATE('/'); setTimeout(() => scrollToSection('sobre'), 100); }} className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Sobre</button>
              <button onClick={() => { window.REACT_APP_NAVIGATE('/'); setTimeout(() => scrollToSection('servicos'), 100); }} className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Serviços</button>
              <button onClick={() => { window.REACT_APP_NAVIGATE('/'); setTimeout(() => scrollToSection('contato'), 100); }} className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">Agendar Consulta</button>
            </nav>
            <button className="md:hidden p-2 text-gray-700 cursor-pointer">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      <main className="pt-20">
        {/* Hero Section */}
        <section className="min-h-[calc(100vh-80px)] flex items-center justify-center bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400">
          <div className="max-w-4xl mx-auto px-6 py-20 text-center">
            <div 
              className="relative inline-block mb-8"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <div className={`w-32 h-32 bg-white rounded-full flex items-center justify-center mx-auto shadow-2xl transition-transform duration-300 ${isHovered ? 'scale-110' : ''}`}>
                <i className="ri-instagram-line text-6xl bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 bg-clip-text text-transparent"></i>
              </div>
              <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center border-4 border-white">
                <i className="ri-check-line text-white text-lg"></i>
              </div>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              @luanlara.psicologo
            </h1>
            
            <p className="text-xl md:text-2xl text-white/90 mb-4">
              Psicólogo CRP 06/223216
            </p>
            
            <p className="text-lg text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed">
              🧠 Psicólogo Clínico & Esportivo • ⚽ Especialista em Psicologia do Futebol • 💻 Atendimento 100% Online
            </p>

            <a
              href="https://www.instagram.com/luanlara.psicologo/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-3 bg-white text-gray-900 px-10 py-5 rounded-full text-xl font-bold hover:bg-gray-100 transition-all hover:scale-105 shadow-2xl whitespace-nowrap"
            >
              <i className="ri-instagram-line text-2xl"></i>
              <span>Seguir no Instagram</span>
            </a>

            <p className="text-white/70 mt-6 text-sm">
              Clique para ver conteúdos exclusivos sobre saúde mental e psicologia do esporte
            </p>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Por que <span className="text-purple-600">Seguir</span>?
              </h2>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                Acompanhe conteúdos exclusivos sobre psicologia clínica e esportiva diretamente no Instagram
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-lightbulb-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Dicas de Saúde Mental</h3>
                <p className="text-gray-600">Conteúdos práticos para seu bem-estar emocional</p>
              </div>

              <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-football-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Psicologia do Futebol</h3>
                <p className="text-gray-600">Insights sobre performance mental no esporte</p>
              </div>

              <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-live-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Lives e Q&A</h3>
                <p className="text-gray-600">Sessões ao vivo para tirar suas dúvidas</p>
              </div>

              <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-heart-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Reflexões</h3>
                <p className="text-gray-600">Textos inspiradores sobre autoconhecimento</p>
              </div>
            </div>

            <div className="text-center">
              <a
                href="https://www.instagram.com/luanlara.psicologo/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-instagram-line text-xl"></i>
                <span>Ver Conteúdos no Instagram</span>
              </a>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Pronto para Começar sua Jornada?
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Siga no Instagram para acompanhar conteúdos exclusivos ou agende sua consulta para um atendimento personalizado.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.instagram.com/luanlara.psicologo/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-instagram-line text-xl"></i>
                <span>Seguir no Instagram</span>
              </a>
              <button
                onClick={() => { window.REACT_APP_NAVIGATE('/'); setTimeout(() => scrollToSection('contato'), 100); }}
                className="bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Agendar Consulta
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 flex-shrink-0">
                  <img
                    src="https://static.readdy.ai/image/9df808c5a5eda92f2f3db0cc4cefbc45/a965b4996718a266233e16beeb49802c.png"
                    alt="Luan Lara Psicólogo Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Luan Lara Psicólogo</h3>
                  <p className="text-gray-400 text-sm">Psicologia Clínica & Esportiva</p>
                </div>
              </div>
              <p className="text-gray-300 mb-4 leading-relaxed">
                Psicólogo especializado em atendimentos clínicos individuais e psicologia do futebol. Consultas 100% online com foco no seu bem-estar e desenvolvimento pessoal ou esportivo.
              </p>
              <div className="flex space-x-4">
                <a 
                  href="https://wa.me/5519982330400?text=Olá%2C%20Luan%20eu%20vim%20do%20site%20luanlarapsicologo%20e%20quero%20saber%20mais%20sobre%20as%20sessões" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                </a>
                <a 
                  href="https://www.instagram.com/luanlara.psicologo/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer"
                >
                  <i className="ri-instagram-line text-lg"></i>
                </a>
                <a 
                  href="https://www.linkedin.com/in/luan-lara-3334aa36a/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer"
                >
                  <i className="ri-linkedin-line text-lg"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Navegação</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => window.REACT_APP_NAVIGATE('/')} className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer">
                    Início
                  </button>
                </li>
                <li>
                  <button onClick={() => { window.REACT_APP_NAVIGATE('/'); setTimeout(() => scrollToSection('sobre'), 100); }} className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer">
                    Sobre
                  </button>
                </li>
                <li>
                  <button onClick={() => { window.REACT_APP_NAVIGATE('/'); setTimeout(() => scrollToSection('servicos'), 100); }} className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer">
                    Serviços
                  </button>
                </li>
                <li>
                  <button onClick={() => { window.REACT_APP_NAVIGATE('/'); setTimeout(() => scrollToSection('contato'), 100); }} className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer">
                    Contato
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Especialidades</h4>
              <ul className="space-y-2 text-gray-300">
                <li>Psicologia Clínica</li>
                <li>Psicologia do Futebol</li>
                <li>Ansiedade e Depressão</li>
                <li>Performance Esportiva</li>
                <li>Autoestima</li>
                <li>Gestão de Estresse</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">© 2025 Luan Lara Psicólogo. Todos os direitos reservados.</p>
              <div className="flex items-center space-x-4 mt-4 md:mt-0">
                <span className="text-gray-400 text-sm">CRP: 06/223216</span>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/instagram')}
                  className="text-gray-400 hover:text-teal-400 transition-colors text-sm cursor-pointer"
                >
                  Me siga lá no Instagram! 👍
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
